import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const PRODUCTS = [
  {
    id: 'enkel-kvartal',
    name: 'enkel kvartal',
    price: '18',
    tagline: 'Vores billigste – kvartalsvis afregning',
    features: ['Spotpris uden tillæg', 'Aconto (fast forudbetaling)', 'Afregning hvert kvartal'],
    highlight: false,
  },
  {
    id: 'enkel-strom',
    name: 'enkel strøm',
    price: '28',
    tagline: 'Mest valgte – månedlig afregning',
    features: ['Spotpris uden tillæg', 'Aconto som standard – forbrug kan vælges', 'Månedlig afregning'],
    highlight: true,
  },
  {
    id: 'enkel-natur',
    name: 'enkel natur',
    price: '48',
    tagline: 'Med donation til naturen',
    features: ['Spotpris uden tillæg', 'Aconto som standard – forbrug kan vælges', '20 kr./md. til Den Danske Naturfond'],
    highlight: false,
  },
];

export default function VelkommenSwitchProducts() {
  const [selected, setSelected] = useState('enkel-strom');
  const [billing, setBilling] = useState('aconto'); // 'aconto' | 'forbrug'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Indtast venligst en gyldig email.');
      return;
    }
    setLoading(true);
    try {
      const res = await base44.functions.invoke('submitProductSwitchRequest', {
        email,
        product: selected,
        billing,
        source_page: 'velkommen',
      });
      if (res?.data?.success) {
        setSubmitted(true);
      } else {
        setError('Noget gik galt. Prøv igen eller kontakt os på hej@enkelenergi.dk.');
      }
    } catch {
      setError('Noget gik galt. Prøv igen eller kontakt os på hej@enkelenergi.dk.');
    }
    setLoading(false);
  };

  const selectedProduct = PRODUCTS.find((p) => p.id === selected);

  return (
    <section id="skift-produkt" className="py-16 md:py-20 bg-white scroll-mt-20">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#eab308] text-[#1a2e1a] text-xs font-bold px-3 py-1 rounded-full mb-3">HELT FRIVILLIGT</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a2e1a] mb-3" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Vil du spare? Skift til en af vores tre normale aftaler
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Du er allerede kunde hos os, så du behøver ikke gøre noget som ny kunde. Vil du beholde overgangsaftalen med forbrugsafregning,
              kører den bare videre. Vil du spare, så vælg en aftale nedenfor og indtast den email, du modtog din velkomstbesked på –
              så flytter vi dig <strong>fra overgangsaftalen til ren spotpris uden tillæg, fra kun 18 kr./md.</strong>{' '}
              Tjek selv prisen i vores{' '}
              <Link to="/elpris-sammenligning" className="text-[#1a2e1a] font-semibold underline">prissammenligning</Link>.
            </p>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed mt-3">
              <strong>Bemærk:</strong> vores tre normale aftaler afregnes som udgangspunkt med <strong>aconto</strong> (fast månedlig forudbetaling).
              Vil du hellere betale bagud for dit faktiske forbrug, kan du vælge forbrugsafregning nedenfor (+2 øre/kWh).
            </p>
          </div>

          {submitted ? (
            <div className="max-w-lg mx-auto bg-[#f5f3ee] border border-[#e8e4d9] rounded-2xl p-8 text-center">
              <div className="w-14 h-14 bg-[#1a2e1a] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-[#eab308]" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Anmodning modtaget!</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vi har modtaget din anmodning om at skifte til <strong>{selectedProduct?.name}</strong>
                {billing === 'forbrug' ? ' med forbrugsafregning' : ''}. Vi behandler den hurtigst muligt og bekræfter
                ændringen på din email, før den træder i kraft. Du skal ikke foretage dig yderligere.
              </p>
            </div>
          ) : (
            <>
              {/* Selectable product cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {PRODUCTS.map((p) => {
                  const isSelected = selected === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => {
                        setSelected(p.id);
                        // enkel kvartal afregnes altid aconto — forbrugsafregning findes ikke på det produkt
                        if (p.id === 'enkel-kvartal') setBilling('aconto');
                      }}
                      className={`text-left rounded-2xl p-6 flex flex-col transition-all ${
                        isSelected
                          ? 'bg-[#f5f3ee] border-2 border-[#1a2e1a] shadow-lg ring-2 ring-[#1a2e1a]/10'
                          : 'bg-[#f5f3ee] border border-[#e8e4d9] hover:border-[#1a2e1a]/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3 min-h-[28px]">
                        {p.highlight ? (
                          <span className="bg-[#eab308] text-[#1a2e1a] text-xs font-bold px-3 py-1 rounded-full">POPULÆR</span>
                        ) : <span />}
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${isSelected ? 'bg-[#1a2e1a] border-[#1a2e1a]' : 'border-gray-300'}`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>{p.name}</h3>
                      <p className="text-gray-500 text-sm mb-4">{p.tagline}</p>
                      <p className="mb-5">
                        <span className="text-3xl font-bold text-[#1a2e1a]">{p.price}</span>
                        <span className="text-sm text-gray-500 ml-1">kr./md.</span>
                      </p>
                      <ul className="space-y-2 flex-1">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-[#1a2e1a] flex-shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>

              {/* Request form */}
              <div className="max-w-lg mx-auto bg-[#f5f3ee] border border-[#e8e4d9] rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-bold text-[#1a2e1a] text-center mb-1" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
                  Anmod om skift til {selectedProduct?.name}
                </h3>
                <p className="text-gray-600 text-sm text-center mb-6">
                  Indtast den email, du normalt modtager dine el-regninger på. Når du sender anmodningen, beder du os om at
                  flytte din eksisterende aftale. Vi bekræfter ændringen på din email, før den træder i kraft.
                </p>

                {/* Billing choice */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-[#1a2e1a] mb-2 text-center">Vælg afregningsform</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setBilling('aconto')}
                      className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${billing === 'aconto' ? 'bg-[#1a2e1a] text-white border-[#1a2e1a]' : 'bg-white text-[#1a2e1a] border-[#e8e4d9] hover:border-[#1a2e1a]/40'}`}
                    >
                      Aconto
                      <span className="block text-[11px] font-normal opacity-80">Fast månedlig forudbetaling</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBilling('forbrug')}
                      disabled={selected === 'enkel-kvartal'}
                      className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${billing === 'forbrug' ? 'bg-[#1a2e1a] text-white border-[#1a2e1a]' : 'bg-white text-[#1a2e1a] border-[#e8e4d9] hover:border-[#1a2e1a]/40'} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#e8e4d9]`}
                    >
                      Forbrugsafregning
                      <span className="block text-[11px] font-normal opacity-80">Betal kun dit forbrug · +2 øre/kWh</span>
                    </button>
                  </div>
                  {selected === 'enkel-kvartal' && (
                    <p className="text-[11px] text-gray-500 text-center mt-2">
                      enkel kvartal afregnes altid med aconto – forbrugsafregning kan ikke vælges på denne aftale.
                    </p>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <Input
                    type="email"
                    placeholder="din@email.dk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                  {error && <p className="text-red-600 text-xs text-center">{error}</p>}
                  <Button type="submit" disabled={loading} className="bg-[#1a2e1a] hover:bg-[#0f1f12] text-white font-semibold h-12 px-6 whitespace-nowrap">
                    {loading ? 'Sender...' : `Anmod om skift til ${selectedProduct?.name}`}
                  </Button>
                  <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                    Dette er en uforpligtende anmodning og påvirker ikke din ret til at opsige den tildelte aftale med 1 måneds varsel.
                  </p>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}